import { type Ref, unref } from "@nuxtjs/composition-api";
import Vue from "vue";
import { type ActionContext, type Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { getModuleFromContext } from "@/store/-utils";
import { type AbTestPair, type FetchFeatureFlagParams, type FetchVariantParams } from "@/types";
import { FEATURE_FLAG_ACTIVE } from "@/utils/experiments/constants";

const moduleName = "experiments";

@Module({
  name: moduleName,
  stateFactory: true,
  namespaced: true,
})
export default class ExperimentsStore extends VuexModule {
  variants: Record<string, AbTestPair> = {};
  flags: Record<string, AbTestPair> = {};

  get variant() {
    return (variantName: string | Ref<string>) => {
      getExperimentsStoreContext(this.context)?.store?.$experiments?.exposure(unref(variantName));

      return this.variants[unref(variantName)];
    };
  }

  get flag() {
    return (flagName: string | Ref<string>) => {
      getExperimentsStoreContext(this.context)?.store?.$experiments?.exposure(unref(flagName));

      return this.flags[unref(flagName)];
    };
  }

  get hasFlag() {
    return (flagName: string) => (this.flag(flagName)?.value ?? "off") === FEATURE_FLAG_ACTIVE;
  }

  @Action({ rawError: true })
  fetchVariant({ variantName, defaultValue = "control" }: FetchVariantParams) {
    if (this.store.$experiments === undefined) {
      return;
    }
    if (this.variants[unref(variantName)] !== undefined) {
      return;
    }

    const variant = this.store.$experiments?.fetchVariant(unref(variantName), unref(defaultValue));

    this.SET_VARIANT({ name: unref(variantName), value: variant.value });
  }

  @Action({ rawError: true })
  fetchFeatureFlag({ flagName, defaultValue = "off" }: FetchFeatureFlagParams) {
    if (this.store.$experiments === undefined) {
      return;
    }
    if (this.flags[unref(flagName)] !== undefined) {
      return;
    }

    const variant = this.store.$experiments?.fetchFeatureFlag(unref(flagName), unref(defaultValue));

    this.SET_FLAG({ name: unref(flagName), value: variant.value });
  }

  @Mutation
  SET_VARIANT({ name, value, payload }: AbTestPair) {
    Vue.set(this.variants, name, { value, payload });
  }

  @Mutation
  SET_FLAG({ name, value, payload }: AbTestPair) {
    Vue.set(this.flags, name, { value, payload });
  }
}

export const getExperimentsStore = (store: Store<unknown>) => {
  return getModule(ExperimentsStore, store);
};

export const getExperimentsStoreContext = (context: ActionContext<unknown, unknown>) => {
  return getModuleFromContext<ExperimentsStore>(context, moduleName);
};
